import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import axiosInstance from '../../common/axiosInstance';
import { DotLoader } from '../../components/loader/Loader';
import { ArrowLeft, Download } from 'lucide-react';
import SplitACChecklistTable, { SplitACChecklistRow } from '../../components/SplitACChecklist/SplitACChecklistTable';
import { usePremisesStore } from '../../store/store';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

const initialItems: SplitACChecklistRow[] = [
    { srNo: 1, particulars: 'Is Split AC running for 24 * 7 ?', status: null, remarks: null },
    { srNo: 2, particulars: 'Check whether split AC is provided with external stabilizer to safeguard voltage fluctuations.', status: null, remarks: null },
    { srNo: 3, particulars: 'To ensure with the OEM that the split Acs are provided with surge arrestor or surge protection device in the PCB.', status: null, remarks: null },
    { srNo: 4, particulars: 'Local MCB (Miniature Circuit Breaker) of split AC near AC shall not be located more than 1.8 meters from the floor level.', status: null, remarks: null },
    { srNo: 5, particulars: 'All the MCB shall be switched off during the end of the day, before employees leave cabin?', status: null, remarks: null },
    { srNo: 6, particulars: 'To check for any noise and vibration of the outdoor compressor unit.', status: null, remarks: null },
    { srNo: 7, particulars: 'Compressor unit shall be free from corrosion', status: null, remarks: null },
    { srNo: 8, particulars: 'Split AC indoor unit shall not be installed in the combustible support', status: null, remarks: null },
    { srNo: 9, particulars: 'Is the compressor current checked through clamp meter during periodical maintenance?', status: null, remarks: null },
    { srNo: 10, particulars: 'Inspecting the electrical connection terminals and PCB control circuit terminal connectors', status: null, remarks: null },
    { srNo: 11, particulars: 'Inspecting the compressor capacitor and PCB board capacitor For its healthiness. To check the continuity.', status: null, remarks: null },
    { srNo: 12, particulars: 'To check the bearing of indoor unit blower cooling fan, condenser fan and compressor and lubricate the parts if needed.', status: null, remarks: null },
    { srNo: 13, particulars: 'To check healthiness of contactors and relays', status: null, remarks: null },
    { srNo: 14, particulars: 'Check condenser coil and condition of condenser fan blades. Cleaning of condenser coil.', status: null, remarks: null },
    { srNo: 15, particulars: 'Cleaning of air filters periodically to avoid choking with dust.', status: null, remarks: null },
    { srNo: 16, particulars: 'Employees to report immediately if there is fall in the cooling Temperature. This can be due to leak in the refrigerant gas, Compromising integrity of cooling coils.', status: null, remarks: null },
    { srNo: 17, particulars: 'To check the condition of Thermostat and voltage across the terminals.', status: null, remarks: null },
    { srNo: 18, particulars: 'To check the condition of capacitor, SMPS, ICU and diode in the PCB With the help of OEM first and get trained from thereon.', status: null, remarks: null },
    { srNo: 19, particulars: 'To check the healthiness of low pressure and high pressure switches and compressor pressure', status: null, remarks: null },
    { srNo: 20, particulars: 'Gas pipeline insulation and cleaning of drain line to prevent clogs', status: null, remarks: null },
    { srNo: 21, particulars: 'To check all the fire extinguishing system, hose reels, hydrant system and smoke detectors are in readily working condition', status: null, remarks: null },
];

const SplitACChecklistForm: React.FC = () => {
    const navigate = useNavigate();
    const { id } = useParams<{ id: string }>();
    const [searchParams] = useSearchParams();
    const isEditMode = id && id !== 'new';
    const isViewMode = isEditMode && !searchParams.get('edit');

    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [name, setName] = useState('');
    const [premisesId, setPremisesId] = useState('');
    const [date, setDate] = useState('');
    const [items, setItems] = useState<SplitACChecklistRow[]>(initialItems);
    const [showDownloadModal, setShowDownloadModal] = useState(false);
    const [customComment, setCustomComment] = useState('');

    const { allPremises, fetchPremises } = usePremisesStore();

    useEffect(() => {
        fetchPremises();
    }, [fetchPremises]);

    useEffect(() => {
        if (isEditMode && id) {
            fetchChecklist(id);
        }
    }, [id, isEditMode]);

    const fetchChecklist = async (checklistId: string) => {
        try {
            setLoading(true);
            const res = await axiosInstance.get(`/api/split-ac-checklist/${checklistId}`);
            if (res.data?.data) {
                setName(res.data.data.name || '');
                setPremisesId(res.data.data.premisesId || res.data.data.premise || '');
                setDate(res.data.data.date);
                if (res.data.data.items && res.data.data.items.length > 0) {
                    setItems(res.data.data.items);
                }
            }
        } catch (err) {
            console.error(err);
            alert('Failed to fetch checklist');
        } finally {
            setLoading(false);
        }
    };

    const handleChange = (index: number, updatedRow: SplitACChecklistRow) => {
        const newItems = [...items];
        newItems[index] = updatedRow;
        setItems(newItems);
    };

    const handleDownloadReport = () => {
        const doc = new jsPDF();
        
        doc.setFontSize(18);
        doc.text("Split AC Maintenance Report", 14, 22);

        doc.setFontSize(10);
        const selectedPremise = allPremises.find((p: any) => p._id === premisesId);
        const premiseNameToUse = selectedPremise ? selectedPremise.premisesName : "N/A";

        doc.text(`Checklist Name: ${name || 'N/A'}`, 14, 32);
        doc.text(`Premise: ${premiseNameToUse}`, 14, 38);
        doc.text(`Date: ${date ? new Date(date).toLocaleDateString() : 'N/A'}`, 14, 44);

        if (customComment) {
            doc.text(`Comments: ${customComment}`, 14, 50);
        }

        const tableColumn = ["Sr. No.", "Particulars", "Status", "Remarks"];
        const tableRows = items.map(item => [
            item.srNo.toString(),
            item.particulars,
            item.status || "N/A",
            item.remarks || "N/A"
        ]);

        autoTable(doc, {
            head: [tableColumn],
            body: tableRows,
            startY: customComment ? 58 : 52,
            styles: { fontSize: 8, cellPadding: 3 },
            headStyles: { fillColor: [0, 168, 165] },
            columnStyles: {
                0: { cellWidth: 15 },
                1: { cellWidth: 80 },
                2: { cellWidth: 30 },
                3: { cellWidth: 'auto' }
            }
        });

        doc.save(`Split_AC_Report_${name || 'Checklist'}.pdf`);
        setShowDownloadModal(false);
        setCustomComment('');
    };

    const handleSave = async () => {
        if (!name || !premisesId || !date) {
            alert('Please fill checklist name, specify premise and fill date');
            return;
        }

        for (const item of items) {
            if (!item.status || item.status.trim() === '') {
                alert(`Please select a status for item ${item.srNo}: "${item.particulars}"`);
                return;
            }
        }

        // Validate remarks
        for (const item of items) {
            if ((item.status === 'Partially Complied' || item.status === 'Not Complied') && (!item.remarks || item.remarks.trim() === '')) {
                alert('Remarks are mandatory for Partially Complied or Not Complied items');
                return;
            }
        }

        try {
            setSaving(true);
            const selectedPremise = allPremises.find((p: any) => p._id === premisesId);
            const premisesName = selectedPremise ? selectedPremise.premisesName : '';

            const payload = {
                name,
                premisesId,
                premisesName,
                date,
                items
            };

            if (isEditMode) {
                await axiosInstance.put(`/api/split-ac-checklist/${id}`, payload);
                alert('Checklist updated successfully');
            } else {
                await axiosInstance.post('/api/split-ac-checklist', payload);
                alert('Checklist created successfully');
            }
            navigate('/safety-equipment/split-ac-checklist');
        } catch (err: any) {
            console.error(err);
            alert(err.response?.data?.error || 'Failed to save checklist');
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return (
            <div className="flex justify-center items-center h-screen">
                <DotLoader />
            </div>
        );
    }

    return (
        <div className="w-full px-6 py-4">
            <div className="w-full mt-20 mb-8">
                <div className="flex items-center justify-between mb-6">
                    <div>
                        <h2 className="text-2xl font-bold text-gray-800 mb-2">
                            {isEditMode && !isViewMode ? "Edit" : isViewMode ? "View" : "Create New"} Split AC Checklist
                        </h2>
                        <p className="text-gray-600">
                            {!isEditMode && !isViewMode
                                ? "Fill in the fields below and save to create a new checklist"
                                : "View and manage split ac maintenance checklist parameters"}
                        </p>
                    </div>
                    <div className="flex gap-3">
                        <button
                            onClick={() => navigate("/safety-equipment/split-ac-checklist")}
                            className="flex items-center gap-2 py-2 px-4 bg-gray-200 text-gray-700 rounded hover:bg-gray-300 transition-colors"
                        >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                            </svg>
                            Back to List
                        </button>
                        {(isViewMode || isEditMode) && (
                            <button
                                onClick={() => setShowDownloadModal(true)}
                                className="flex items-center gap-2 py-2 px-4 bg-[#00A8A5] text-white rounded hover:bg-[#008A87] transition-colors shadow-lg hover:shadow-xl"
                            >
                                <Download size={20} />
                                Download Report
                            </button>
                        )}
                        {!isViewMode && (
                            <button
                                onClick={handleSave}
                                disabled={saving}
                                className="flex items-center gap-2 py-2 px-4 bg-[#00A8A5] text-white rounded hover:bg-[#008A87] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                {saving ? (
                                    <>
                                        <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                        </svg>
                                        Saving...
                                    </>
                                ) : (
                                    <>
                                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                        </svg>
                                        {isEditMode ? "Update" : "Save"} Checklist
                                    </>
                                )}
                            </button>
                        )}
                    </div>
                </div>
            </div>

            <div className="bg-white rounded-lg shadow-lg p-6 mb-6 max-w-7xl mx-auto">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Checklist Name <span className="text-red-500">*</span></label>
                        <input
                            type="text"
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            disabled={!!isViewMode}
                            placeholder="Enter Checklist Name"
                            className={`w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#00A8A5] focus:border-transparent ${!!isViewMode ? "bg-gray-100 cursor-not-allowed" : ""
                                }`}
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Premises <span className="text-red-500">*</span></label>
                        <select
                            value={premisesId}
                            onChange={(e) => setPremisesId(e.target.value)}
                            disabled={!!isViewMode}
                            className={`w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#00A8A5] focus:border-transparent ${!!isViewMode ? "bg-gray-100 cursor-not-allowed" : ""
                                }`}
                        >
                            <option value="">Select Premise</option>
                            {allPremises.map((p: any) => (
                                <option key={p._id} value={p._id}>{p.premisesName}</option>
                            ))}
                        </select>
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Date <span className="text-red-500">*</span></label>
                        <input
                            type="date"
                            value={date}
                            onChange={(e) => setDate(e.target.value)}
                            disabled={!!isViewMode}
                            className={`w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#00A8A5] focus:border-transparent ${!!isViewMode ? "bg-gray-100 cursor-not-allowed" : ""
                                }`}
                        />
                    </div>
                </div>
            </div>

            <div className="bg-white rounded-lg shadow-lg p-6 max-w-7xl mx-auto">

                <SplitACChecklistTable
                    data={items}
                    onChange={handleChange}
                    isViewMode={!!isViewMode}
                />
            </div>

            {showDownloadModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-6">
                        <h3 className="text-xl font-bold text-gray-800 mb-4">Download Report</h3>
                        <p className="text-sm text-gray-600 mb-4">Add an optional custom comment to include in the PDF report.</p>
                        <textarea
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#00A8A5] mb-6 min-h-[100px]"
                            placeholder="Enter your comment here..."
                            value={customComment}
                            onChange={(e) => setCustomComment(e.target.value)}
                        />
                        <div className="flex justify-end gap-3">
                            <button
                                onClick={() => {
                                    setShowDownloadModal(false);
                                    setCustomComment('');
                                }}
                                className="px-4 py-2 rounded text-gray-600 hover:bg-gray-100 transition-colors"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleDownloadReport}
                                className="px-4 py-2 bg-[#00A8A5] text-white rounded hover:bg-[#008A87] transition-colors"
                            >
                                Download PDF
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default SplitACChecklistForm;
